import React, { useCallback, useEffect, useMemo, useState } from "react";
import { requestExternalUser } from "@microdoing/shared/services/externalProfile/actions";
import { updateFilesToUpload } from "@microdoing/shared/services/upload/actions";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import theme from "app/Theme/Theme";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import i18n from "@microdoing/shared/i18n";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import {
  getUserId,
  getUserFirstName,
  getUserProfilePictureUrl,
} from "@microdoing/shared/services/user/selectors";
import {
  commentReviewRequest,
  seeCommentsReviewRequest,
} from "@microdoing/shared/services/userCampaign/actions";
import { useLaunchCamera } from "../FeedbackScreen/useLaunchCamera";
import { recordAudio } from "./useRecordAudio";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { Box, MySafeAreaView } from "app/components/common";
import { getFeedbackSelected } from "@microdoing/shared/services/historical/selector";
import Header from "./Header";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsActionAfestReviewChatScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import Chat from "./ChatScreen/Chat";
import { InputChat } from "./InputChat";

const ReviewChatScreen = ({}) => {
  const [text, setText] = useState("");
  const feedbackSelected = useSelector(getFeedbackSelected);
  const stepFeedbackId = feedbackSelected._id;
  const {
    corrector = {
      name: "",
      picture: "",
      correctorId: "",
    },
    comments,
  } = feedbackSelected?.review || {};

  const language = useSelector(getLanguage);
  const userId = useSelector(getUserId);
  const userPicture = useSelector(getUserProfilePictureUrl);
  const userName = useSelector(getUserFirstName);
  const filesToUpload = useSelector(getFilesToUpload);

  const disableComposer = useMemo(() => {
    if (filesToUpload.length > 0) {
      return true;
    }
    return !corrector?.correctorId;
  }, [filesToUpload, corrector]);

  const dispatch = useDispatch();

  const [messages, setMessages] = useState(comments);

  const [error, setError, openNativeModule] = useLaunchCamera({});
  const [
    errorRecord,
    setErrorRecord,
    takeAudio,
    onStartRecording,
    onStopRecording,
    pauseRecording,
    audioDuration,
    isAudioActivated,
  ] = recordAudio();

  useEffect(() => {
    updateFilesToUpload([]);
    setText("");
  }, []);

  useEffect(() => {
    dispatch(seeCommentsReviewRequest({ stepFeedbackId }));

    return () => {
      dispatch(updateFilesToUpload([]));
    };
  }, [comments.length]);

  useEffect(() => {
    const newCommentsLength = comments.length;
    const newComments = [
      ...comments,
      ...messages.slice(newCommentsLength - 1, messages.length - 1),
    ];
    setMessages(newComments);
  }, [comments]);

  const navigateExternalProfile = useCallback(
    () => () => {
      dispatch(requestExternalUser({ userId: corrector.correctorId }));
    },
    [corrector.correctorId],
  );

  const onSendMessage = message => {
    const newMessage = {
      text: message?.text,
      video: message?.video,
      image: message?.image,
      _id: moment().format(),
      createdAt: moment().toDate(),
      isSeen: false,
      user: {
        _id: userId,
        avatar: userPicture,
        name: userName,
      },
      pending: true,
    };
    setMessages([...messages, newMessage]);
    dispatch(
      commentReviewRequest({
        stepFeedbackId,
        coachId: corrector?.correctorId,
        comment: newMessage,
        userId,
      }),
    );
    setText("");
  };

  const handleCancelSelection = assetIndex => () => {
    const assets = filesToUpload.filter((asset, index) => index !== assetIndex);
    dispatch(updateFilesToUpload(assets));
  };

  const handlePressAudio = async () => {
    takeAudio();
  };

  const cancelAudio = () => {
    pauseRecording();
  };

  const sendAudio = () => {
    onStopRecording();
    setTimeout(() => {
      onSendMessage([{ text: "" }]);
    }, 500);
  };

  const nullFunction = useCallback(() => null, []);

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsActionAfestReviewChatScreen}>
      <MySafeAreaView
        flex={1}
        backgroundColor="neutral.white"
        padding={isWebDesktop ? "sm" : "null"}
      >
        <Header
          hasBackArrow={!isWebDesktop}
          testID="chat-back-button"
          title={corrector?.name}
          receivers={[corrector]}
          clientName=""
          navigateProfile={navigateExternalProfile}
        />
        <Chat
          isReviewChat={true}
          messages={messages}
          handlePressAvatar={navigateExternalProfile}
          setFocusedMessage={nullFunction}
          displayUnsend={nullFunction}
          onLoadEarlier={nullFunction}
          isLoadingEarlier={false}
          requesting={false}
        />
        <InputChat
          placeholderTextColor={theme.colors["neutral.600"]}
          filesToUpload={filesToUpload}
          openNativeModule={openNativeModule}
          onTextChanged={setText}
          handlePressAudio={handlePressAudio}
          handleCancelSelection={handleCancelSelection}
          cancelAudio={cancelAudio}
          sendAudio={sendAudio}
          text={text}
          isAudioActivated={isAudioActivated}
          audioDuration={audioDuration}
          onSend={onSendMessage}
        />
      </MySafeAreaView>
    </WebDesktopView>
  );
};

export default ReviewChatScreen;
